// /reset-password — without a token: ask for a reset link
// (POST /auth/forgot-password); with ?token=… (link from the reset mail,
// send_password_reset_email): set a new password (POST /auth/reset-password).
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { post } from '../../lib/api.js';
import { errorMessage } from '../../lib/errors.js';
import { MIN_PASSWORD } from '../../lib/auth.jsx';
import { useLang, useT } from '../../lib/i18n.jsx';
import PublicCard, { Notice } from './PublicCard.jsx';

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get('token');
  return token ? <NewPassword token={token} /> : <RequestLink />;
}

function RequestLink() {
  const t = useT();
  const { lang } = useLang();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr('');
    try {
      // The API answers the same whether or not the address exists.
      await post('/auth/forgot-password', { email: email.trim() });
      setSent(true);
    } catch (e2) {
      setErr(errorMessage(e2, { lang }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <PublicCard title={t('reset.request.title')} subtitle={sent ? null : t('reset.request.sub')}>
      {sent ? (
        <Notice>{t('reset.request.sent', { email: email.trim() })}</Notice>
      ) : (
        <form className="login-form" onSubmit={submit}>
          {err && <Notice tone="error">{err}</Notice>}
          <div>
            <label htmlFor="email">{t('account.profile.email')}</label>
            <input id="email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="login-actions">
            <span />
            <button type="submit" className="btn-primary" disabled={busy}>
              {busy ? t('account.working') : t('reset.request.submit')}
            </button>
          </div>
        </form>
      )}
    </PublicCard>
  );
}

function NewPassword({ token }) {
  const t = useT();
  const { lang } = useLang();
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (pw.length < MIN_PASSWORD) return setErr(t('public.password.tooShort', { n: MIN_PASSWORD }));
    if (pw !== pw2) return setErr(t('public.password.mismatch'));
    setBusy(true);
    try {
      await post('/auth/reset-password', { token, new_password: pw });
      setDone(true);
    } catch (e2) {
      setErr(e2?.status === 400 ? t('reset.invalid') : errorMessage(e2, { lang }));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <PublicCard title={t('reset.title')}>
        <Notice>{t('reset.done')}</Notice>
        <Link className="btn-primary public-cta" to="/login">{t('public.toLogin')}</Link>
      </PublicCard>
    );
  }

  return (
    <PublicCard
      title={t('reset.title')}
      subtitle={t('reset.sub')}
      footer={<Link to="/reset-password">{t('reset.requestNew')}</Link>}
    >
      <form className="login-form" onSubmit={submit}>
        {err && <Notice tone="error">{err}</Notice>}
        <div>
          <label htmlFor="pw">{t('public.password.new')}</label>
          <input id="pw" type="password" autoComplete="new-password" required minLength={MIN_PASSWORD} value={pw} onChange={(e) => setPw(e.target.value)} />
          <p className="field-hint">{t('public.password.hint', { n: MIN_PASSWORD })}</p>
        </div>
        <div>
          <label htmlFor="pw2">{t('public.password.repeat')}</label>
          <input id="pw2" type="password" autoComplete="new-password" required value={pw2} onChange={(e) => setPw2(e.target.value)} />
        </div>
        <div className="login-actions">
          <span />
          <button type="submit" className="btn-primary" disabled={busy}>
            {busy ? t('account.working') : t('reset.submit')}
          </button>
        </div>
      </form>
    </PublicCard>
  );
}
